import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { reportCaughtError } from '../observability/errorReporting';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * The last thing between a thrown render and a blank page. React unmounts the whole tree when a render throws,
 * so without this the interface becomes a white screen with the reason only in a console nobody has open.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportCaughtError(error, info.componentStack ?? undefined);
  }

  render(): ReactNode {
    return this.state.failed ? <CrashNotice /> : this.props.children;
  }
}

/** Separate and functional so it can be translated: a class component cannot use hooks. */
function CrashNotice() {
  const { t } = useTranslation();

  return (
    <div className="app-main">
      <div className="alert alert-danger d-flex gap-2" role="alert">
        <i className="pi pi-exclamation-triangle mt-1" aria-hidden="true" />
        <div>
          <p className="mb-2">{t('app.crashed')}</p>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => window.location.reload()}>
            {t('app.reload')}
          </button>
        </div>
      </div>
    </div>
  );
}
